import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./Account.css";

export default function Account() {
  const navigate = useNavigate();

  const {
    user,
    isLoggedIn,
    authLoading,
    authError,
    clearError,
    updateAccount,
    updateMarketingPreference,
    logout,
  } = useAuth();

  const [formData, setFormData] = useState({
    fullName: "",
    billingEmail: "",
    marketingOptIn: false,
  });

  const [localMessage, setLocalMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    document.title = "My Account | MayaChitro";
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || "",
        billingEmail: user.billingEmail || user.email || "",
        marketingOptIn: Boolean(user.marketingOptIn),
      });
    }
  }, [user]);

  useEffect(() => {
    clearError();
    setLocalMessage("");
    setSuccessMessage("");
  }, [clearError]);

  if (authLoading && !user) {
    return (
      <main className="account-page">
        <div className="account-loading-card">
          <p className="account-eyebrow">MayaChitro Account</p>
          <h1>Checking your account...</h1>
          <p>Please wait a moment.</p>
        </div>
      </main>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login?redirect=/account" replace />;
  }

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));

    setLocalMessage("");
    setSuccessMessage("");
    if (authError) clearError();
  };

  const validateForm = () => {
    if (!formData.fullName.trim()) {
      return "Full name is required.";
    }

    if (
      formData.billingEmail.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billingEmail.trim())
    ) {
      return "Please enter a valid billing email.";
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationMessage = validateForm();

    if (validationMessage) {
      setLocalMessage(validationMessage);
      return;
    }

    const result = await updateAccount({
      fullName: formData.fullName.trim(),
      billingEmail: formData.billingEmail.trim() || user.email,
      marketingOptIn: formData.marketingOptIn,
    });

    if (result.success) {
      setSuccessMessage("Account updated successfully.");
    } else {
      setLocalMessage(result.message || "Could not update account.");
    }
  };

  const handleMarketingToggle = async () => {
    const nextValue = !formData.marketingOptIn;

    setFormData((current) => ({
      ...current,
      marketingOptIn: nextValue,
    }));

    const result = await updateMarketingPreference(nextValue);

    if (result.success) {
      setSuccessMessage(
        nextValue
          ? "You are subscribed to MayaChitro product updates."
          : "You are unsubscribed from MayaChitro product updates."
      );
    } else {
      setLocalMessage(result.message || "Could not update marketing preference.");

      setFormData((current) => ({
        ...current,
        marketingOptIn: !nextValue,
      }));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const messageToShow = authError || localMessage;

  return (
    <main className="account-page">
      <section className="account-hero">
        <div>
          <p className="account-eyebrow">MayaChitro Account</p>
          <h1>Welcome, {user?.fullName || "MayaChitro Customer"}</h1>
          <p>
            Manage your wishlist, order history, download access, billing email,
            and product update preferences from one place.
          </p>
        </div>

        <button className="account-logout-btn" type="button" onClick={handleLogout}>
          Logout
        </button>
      </section>

      <section className="account-dashboard-grid">
        <Link to="/wishlist" className="account-stat-card">
          <span>Wishlist</span>
          <strong>Saved visuals</strong>
          <small>View your favorite photography and wall art.</small>
        </Link>

        <Link to="/my-orders" className="account-stat-card">
          <span>Orders</span>
          <strong>Purchase history</strong>
          <small>Check previous orders and license purchases.</small>
        </Link>

        <Link to="/my-downloads" className="account-stat-card">
          <span>Downloads</span>
          <strong>Access files</strong>
          <small>Find purchased digital files and download links.</small>
        </Link>

        <Link to="/shop" className="account-stat-card highlighted">
          <span>Shop</span>
          <strong>Browse more</strong>
          <small>Explore new photos, posters, and wall art.</small>
        </Link>
      </section>

      <section className="account-main-layout">
        <div className="account-panel">
          <div className="account-panel-heading">
            <p className="account-eyebrow">Profile</p>
            <h2>Account Details</h2>
            <p>
              Keep your name and billing email updated for order delivery,
              receipts, and download access.
            </p>
          </div>

          {messageToShow && <div className="account-alert error">{messageToShow}</div>}
          {successMessage && <div className="account-alert success">{successMessage}</div>}

          <form className="account-form" onSubmit={handleSubmit}>
            <label>
              Full name
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Your full name"
                autoComplete="name"
                required
              />
            </label>

            <label>
              Login email
              <input type="email" value={user?.email || ""} disabled />
              <small>This is your account login email.</small>
            </label>

            <label>
              Billing email
              <input
                type="email"
                name="billingEmail"
                value={formData.billingEmail}
                onChange={handleChange}
                placeholder="Billing or receipt email"
                autoComplete="email"
              />
              <small>
                Used for receipts, order communication, and download support.
              </small>
            </label>

            <label className="account-check-row">
              <input
                type="checkbox"
                name="marketingOptIn"
                checked={formData.marketingOptIn}
                onChange={handleChange}
              />
              <span>
                Send me new photo stories, wall art releases, discounts, and
                MayaChitro product updates.
              </span>
            </label>

            <button className="account-save-btn" type="submit" disabled={authLoading}>
              {authLoading ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>

        <aside className="account-side-panel">
          <div className="account-side-box">
            <p className="account-eyebrow">Marketing Preference</p>
            <h3>
              {formData.marketingOptIn
                ? "Product updates enabled"
                : "Product updates disabled"}
            </h3>
            <p>
              You control whether MayaChitro can send product updates, new
              releases, discounts, and photo story emails.
            </p>

            <button
              type="button"
              className={
                formData.marketingOptIn
                  ? "account-toggle-btn active"
                  : "account-toggle-btn"
              }
              onClick={handleMarketingToggle}
              disabled={authLoading}
            >
              {formData.marketingOptIn ? "Unsubscribe" : "Subscribe"}
            </button>
          </div>

          <div className="account-side-box">
            <p className="account-eyebrow">Account Status</p>
            <ul className="account-status-list">
              <li>
                <span>Account</span>
                <strong>{user?.status || "active"}</strong>
              </li>
              <li>
                <span>Email verified</span>
                <strong>{user?.emailVerified ? "Yes" : "Not yet"}</strong>
              </li>
              <li>
                <span>Role</span>
                <strong>{user?.role || "customer"}</strong>
              </li>
            </ul>
          </div>

          <div className="account-side-box">
            <p className="account-eyebrow">Need Help?</p>
            <h3>Order or download support</h3>
            <p>
              If you cannot find a download or order, contact MayaChitro support
              with your purchase email.
            </p>
            <Link to="/contact" className="account-contact-link">
              Contact Support →
            </Link>
          </div>
        </aside>
      </section>
    </main>
  );
}